import { setIframeSrcDoc } from 'polyfills/helpers';
import { MESSAGE_POLL_SETTINGS } from 'widget_main/app/constants';
import { CSP_NONCE_ALLOWED_URLS } from 'helpers/nonce/helpers';

export const appendElementToDocument = (
  element: HTMLElement,
  callback?: () => void,
) => {
  const scriptEmbedElement = document.querySelector<HTMLScriptElement>(
    '[data-kustomer-api-key]',
  );

  if (!scriptEmbedElement || !scriptEmbedElement.parentNode) return;

  scriptEmbedElement.parentNode.insertBefore(element, scriptEmbedElement);
  callback?.();
};

/**
 * Applies CSP nonce to dynamically loaded scripts based on their src URL.
 *
 * This function intercepts various DOM manipulation methods to automatically add
 * CSP nonces to script elements that have src attributes matching the allowed URLs.
 * This is essential for CSP compliance when using strict Content Security Policies.
 *
 * The function works by:
 * 1. Overriding Node.prototype.appendChild and insertBefore to catch scripts added to DOM
 * 2. Overriding document.head/body.appendChild for specific targeting
 * 3. Intercepting webpack's dynamic chunk loading mechanism
 *
 * URL filtering logic:
 * - Only scripts with src attributes that start with allowed base URLs get nonces
 * - Scripts without src attributes are skipped (inline scripts handled separately)
 * - If no allowedUrls provided, applies nonce to all scripts (backward compatibility)
 * - URL filtering provides security by limiting nonce application to trusted domains
 *
 * IMPORTANT: This function is idempotent - it will only execute once per page load,
 * regardless of how many times it's called. Subsequent calls will be ignored to
 * prevent DOM method override chains and memory leaks.
 *
 * @param nonce - The CSP nonce value to apply to matching scripts
 */
// Track whether the nonce application has already been initialized
// This ensures the function is idempotent and prevents multiple DOM method overrides
let areCSPOverridesApplied = false;

// Export for testing purposes only
export const resetAreCSPOverridesAppliedState = () => {
  areCSPOverridesApplied = false;
};

export const applyNonceToDynamicScripts = (nonce?: string) => {
  // Early return if no nonce provided
  if (!nonce) return;

  // Guard against multiple calls to ensure idempotency
  if (areCSPOverridesApplied) {
    return;
  }

  // Mark as applied to prevent future calls
  areCSPOverridesApplied = true;

  /**
   * Checks if a script should receive a nonce.
   *
   * This function implements the script filtering logic:
   * - Returns true if script has no src attribute (inline scripts always get nonces)
   * - Returns true if script src starts with any allowed base URL
   * - Returns false otherwise (security: don't apply nonce to untrusted domains)
   *
   * @param scriptSrc - The src attribute of the script element
   * @returns boolean indicating whether the script should receive a nonce
   */
  const isScriptAllowed = (scriptSrc?: string) => {
    if (!scriptSrc) {
      return true; // No src, apply nonce to inline scripts
    }

    const isAllowed = CSP_NONCE_ALLOWED_URLS.some(pattern => {
      if (typeof pattern === 'string') {
        return scriptSrc.startsWith(pattern);
      }
      return false;
    });

    return isAllowed;
  };

  /**
   * Override Node.prototype.appendChild to catch scripts added to any DOM node.
   *
   * This is a comprehensive approach that catches scripts added via appendChild
   * regardless of which node they're being added to. This covers most dynamic
   * script insertion methods used by frameworks and libraries.
   */
  const originalAppendChild = Node.prototype.appendChild;
  Node.prototype.appendChild = function(this: Node, child: Node) {
    // Only process script elements that don't already have a nonce
    if (child instanceof HTMLScriptElement && !child.hasAttribute('nonce')) {
      const scriptSrc = child.getAttribute('src') || undefined;
      if (isScriptAllowed(scriptSrc)) {
        child.setAttribute('nonce', nonce);
      }
    }
    return originalAppendChild.call(this, child);
  } as typeof Node.prototype.appendChild;

  /**
   * Override Node.prototype.insertBefore to catch scripts inserted before other elements.
   *
   * This covers scripts added via insertBefore, which is another common method
   * for dynamically inserting script elements into the DOM.
   */
  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function(this: Node, newNode: Node, referenceNode: Node | null) {
    // Only process script elements that don't already have a nonce
    if (newNode instanceof HTMLScriptElement && !newNode.hasAttribute('nonce')) {
      const scriptSrc = newNode.getAttribute('src') || undefined;
      if (isScriptAllowed(scriptSrc)) {
        newNode.setAttribute('nonce', nonce);
      }
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  } as typeof Node.prototype.insertBefore;

  /**
   * Override document.head.appendChild for targeted head script interception.
   *
   * Many frameworks and libraries specifically add scripts to document.head,
   * so this provides a more targeted approach for head-based script insertion.
   */
  const originalHeadAppendChild = document.head.appendChild;
  document.head.appendChild = function(child: Node) {
    if (child instanceof HTMLScriptElement && !child.hasAttribute('nonce')) {
      const scriptSrc = child.getAttribute('src') || undefined;
      if (isScriptAllowed(scriptSrc)) {
        child.setAttribute('nonce', nonce);
      }
    }
    return originalHeadAppendChild.call(document.head, child);
  } as typeof document.head.appendChild;

  /**
   * Override document.body.appendChild for targeted body script interception.
   *
   * Some scripts are added directly to document.body, so this provides
   * targeted coverage for body-based script insertion.
   */
  const originalBodyAppendChild = document.body.appendChild;
  document.body.appendChild = function(child: Node) {
    if (child instanceof HTMLScriptElement && !child.hasAttribute('nonce')) {
      const scriptSrc = child.getAttribute('src') || undefined;
      if (isScriptAllowed(scriptSrc)) {
        child.setAttribute('nonce', nonce);
      }
    }
    return originalBodyAppendChild.call(document.body, child);
  } as typeof document.body.appendChild;

  /**
   * Override document.body.append for targeted body script interception.
   *
   * The append method is a newer DOM API that can add multiple nodes at once.
   * This override ensures scripts added via append also receive CSP nonces.
   */
  const originalBodyAppend = document.body.append;
  document.body.append = function(...nodes: (Node | string)[]) {
    nodes.forEach(node => {
      if (node instanceof HTMLScriptElement && !node.hasAttribute('nonce')) {
        const scriptSrc = node.getAttribute('src') || undefined;
        if (isScriptAllowed(scriptSrc)) {
          node.setAttribute('nonce', nonce);
        }
      }
    });
    return originalBodyAppend.call(document.body, ...nodes);
  } as typeof document.body.append;

  /**
   * Override document.head.append for targeted head script interception.
   *
   * The append method is a newer DOM API that can add multiple nodes at once.
   * This override ensures scripts added via append to head also receive CSP nonces.
   */
  const originalHeadAppend = document.head.append;
  document.head.append = function(...nodes: (Node | string)[]) {
    nodes.forEach(node => {
      if (node instanceof HTMLScriptElement && !node.hasAttribute('nonce')) {
        const scriptSrc = node.getAttribute('src') || undefined;
        if (isScriptAllowed(scriptSrc)) {
          node.setAttribute('nonce', nonce);
        }
      }
    });
    return originalHeadAppend.call(document.head, ...nodes);
  } as typeof document.head.append;

  /**
   * Intercept webpack's dynamic chunk loading mechanism.
   *
   * Webpack uses a special mechanism for loading code splits and dynamic imports.
   * This override specifically targets webpack's chunk loading to ensure that
   * dynamically loaded webpack chunks also receive the appropriate CSP nonce.
   *
   * The webpack chunk loading process:
   * 1. __webpack_require__.e() is called to load a chunk
   * 2. Webpack creates script tags with the chunk ID in the src
   * 3. This override finds those scripts and applies the nonce
   */
  // eslint-disable-next-line no-underscore-dangle
  if ((window as any).__webpack_require__ && (window as any).__webpack_require__.e) {
    // eslint-disable-next-line no-underscore-dangle
    const originalEnsure = (window as any).__webpack_require__.e;
    // eslint-disable-next-line no-underscore-dangle
    (window as any).__webpack_require__.e = function(chunkId: string) {
      return originalEnsure.call(this, chunkId).then(function() {
        // Use setTimeout to ensure scripts are added to DOM before we process them
        setTimeout(function() {
          // Find all scripts that contain the chunk ID in their src
          const scripts = document.querySelectorAll(`script[src*="${chunkId}"]`);
          scripts.forEach(function(script) {
            // Only apply nonce if script doesn't already have one
            if (!script.hasAttribute('nonce')) {
              const scriptSrc = script.getAttribute('src') || undefined;
              if (isScriptAllowed(scriptSrc)) {
                script.setAttribute('nonce', nonce);
              }
            }
          });
        }, 0);
      });
    };
  }
};

// eslint-disable-next-line import/prefer-default-export
export const setupIframeForApi = (
  iframeId: string,
  iframeStyles: string,
  content: string,
  iframeTitle: string,
  nonce?: string
) => {
  const iframe = document.createElement('iframe');

  iframe.setAttribute('id', iframeId);
  iframe.setAttribute('title', iframeTitle);
  iframe.setAttribute('aria-label', iframeTitle);
  iframe.setAttribute('role', 'application');
  iframe.setAttribute('tabindex', '0');
  if (nonce) {
    iframe.setAttribute('nonce', nonce);
  }

  iframe.style.cssText = iframeStyles;

  appendElementToDocument(iframe, () => {
    setIframeSrcDoc(iframe, content);
  });
};

export const getIframeForWidgetMain = (): HTMLIFrameElement | undefined => {
  const iframe = document.getElementById(
    'kustomer-ui-sdk-iframe',
  ) as HTMLIFrameElement;

  if (!iframe) return undefined;

  return iframe;
};

export const getIframeForCoreApi = () => {
  const { frameElement } = window;
  const iframe = (
    frameElement?.nodeName === 'IFRAME'
      ? frameElement
      : document.getElementById('kustomer-core-sdk-iframe')
  ) as HTMLIFrameElement | undefined;

  if (iframe?.nodeName !== 'IFRAME') return undefined;

  return iframe;
};

export const setTrackingTokenInLocalStorage = (token) => {
  try {
    localStorage.setItem('kustomer-chat-tracking-token', token);
  } catch {
    throw new Error('Setting tracking token in local storage');
  }
};

export const getTrackingTokenFromLocalStorage = () => {
  try {
    return localStorage.getItem('kustomer-chat-tracking-token');
  } catch {
    throw new Error('Getting tracking token from local storage');
  }
};

export const removeTrackingTokenFromLocalStorage = () => {
  try {
    return localStorage.removeItem('kustomer-chat-tracking-token');
  } catch {
    throw new Error('Removing tracking token from local storage');
  }
};

/// define the key for pollin ginterval local storage
const POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY =
  'kustomer-initial-chat-polling-interval-ms';

/**
 * Removes the polling interval duration (in milliseconds) from local storage.
 * This is used to clear the stored polling interval setting.
 *
 * @throws {Error} If an error occurs while removing the polling interval.
 */
export const removePollingIntervalMsFromLocalStorage = () => {
  try {
    return localStorage.removeItem(POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY);
  } catch {
    return console.warn(
      'Error removing polling interval MS from local storage',
    );
  }
};

/**
 * Retrieves the polling interval duration (in milliseconds) from local storage.
 * If the value is not found or an error occurs, it returns the default polling interval.
 *
 * @returns {number} The polling interval in milliseconds, either from local storage or the default setting.
 */
export const getPollingIntervalMsFromLocalStorage = () => {
  try {
    const pollingIntervalMsString = localStorage.getItem(
      POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY,
    );
    if (pollingIntervalMsString === null) return MESSAGE_POLL_SETTINGS.interval;
    return parseInt(pollingIntervalMsString, 10);
  } catch {
    // no matter what, return a value - in this case, default to previous behavior
    removePollingIntervalMsFromLocalStorage(); // remove, cleanup
    return MESSAGE_POLL_SETTINGS.interval;
  }
};

/**
 * Stores the polling interval duration (in milliseconds) in local storage.
 * This value determines the interval at which polling operations should occur.
 *
 * @param {string} pollingIntervalMsString - The polling interval duration in milliseconds, stored as a string.
 * @throws {Error} If an error occurs while setting the polling interval.
 */
export const setPollingIntervalMsFromLocalStorage = (
  pollingIntervalMsString: string,
) => {
  try {
    return localStorage.setItem(
      POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY,
      pollingIntervalMsString,
    );
  } catch {
    return console.warn('Error setting polling interval MS in local storage');
  }
};

interface NavigatorWithStandalone extends Navigator {
  standalone: boolean;
}

/**
 * Currently only needed as a check for iOS where we are seeing sentry errors caused by embedded views
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator#navigator.standalone
 */
export const isInAppBrowser = () => {
  // this is something of a hack to get around TypeScript for a Safari/WebKit-only
  const userAgent = window.navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);
  if (!ios) return false;

  // If the device is considered "standalone", then we are in an embedded view
  return (
    'standalone' in window.navigator &&
    (window.navigator as NavigatorWithStandalone).standalone
  );
};

// In case the parent window is of a different origin
export function getSafeParentContext(windowParam: Window): Window | undefined {
  const win = windowParam ?? window;
  try {
    // Check if parent exists and has a location object accessible (won't throw cross-origin)
    if (win.parent && win.parent.location && win.parent.location.href) {
      return win.parent;
    }
  } catch (e) {
    return undefined;
  }

  // No parent or parent is the same window
  return undefined;
}
